import { SearchOutlined } from "@ant-design/icons";
import { Input, Select, Tag } from "antd";
import { CSSProperties, FC, useEffect, useMemo, useRef, useState } from "react";
import { VariableSizeList } from "react-window";
import {
  TIMELINE_DAY_MS,
  TaskTimelineRange,
  TimelineBounds,
  getTaskTimelineRange,
  getTimelineBounds,
} from "../../timeline";
import TaskPriorityTag, { TaskPriority } from "../TaskPriorityTag";
import "./index.css";

type TimelineScale = "day" | "week" | "month";
type TimelineStatus = "todo" | "doing" | "blocked" | "review" | "done" | "discarded";

export interface ProjectTimelineTask {
  id?: string;
  title: string;
  assignees: string[];
  created_at?: number;
  due_time?: number;
  end_time?: number;
  status: TimelineStatus;
  priority?: TaskPriority;
}

interface ProjectTimelineViewProps {
  tasks: ProjectTimelineTask[];
  getMemberName: (userId: string) => string;
  onTaskClick: (task: ProjectTimelineTask) => void;
}

interface TimelineRowData {
  bounds: TimelineBounds;
  chartWidth: number;
  getMemberName: (userId: string) => string;
  onTaskClick: (task: ProjectTimelineTask) => void;
  ranges: TaskTimelineRange[];
  tasks: ProjectTimelineTask[];
  todayLeft?: number;
  totalWidth: number;
}

const INFO_WIDTH = 380;
const MIN_CHART_WIDTH = 760;

const statusText: Record<TimelineStatus, string> = {
  todo: "待处理",
  doing: "进行中",
  blocked: "阻塞",
  review: "评审中",
  done: "已完成",
  discarded: "已废弃",
};

function uniqueValues(values: string[] = []) {
  return Array.from(new Set(values.filter(Boolean)));
}

function formatDate(timestamp?: number) {
  if (!timestamp) return "未设置";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(timestamp));
}

function getRowHeight(task: ProjectTimelineTask) {
  const memberRows = Math.max(1, Math.ceil(uniqueValues(task.assignees).length / 3));
  return 84 + memberRows * 28;
}

function TimelineRow({
  data,
  index,
  style,
}: {
  data: TimelineRowData;
  index: number;
  style: CSSProperties;
}) {
  const task = data.tasks[index];
  const range = data.ranges[index];
  const duration = Math.max(data.bounds.end - data.bounds.start, 1);
  const left = ((range.start - data.bounds.start) / duration) * data.chartWidth;
  const width = Math.max(((range.end - range.start) / duration) * data.chartWidth, 34);
  const rowNumber = String(index + 1).padStart(2, "0");
  const members = uniqueValues(task.assignees);
  const priority = task.priority || "medium";

  return (
    <div
      className={`projectTimelineRow projectTimelineRow--${task.status}`}
      onClick={() => data.onTaskClick(task)}
      style={{ ...style, width: data.totalWidth }}
    >
      <div className="projectTimelineRow__info">
        <span className={`projectTimelineRow__index projectTimelineRow__index--${task.status}`}>
          {rowNumber}
        </span>
        <div className="projectTimelineRow__identity">
          <div className="projectTimelineRow__titleLine">
            <strong title={task.title}>{task.title}</strong>
            <TaskPriorityTag priority={priority} />
          </div>
          <span className="projectTimelineRow__meta">
            {statusText[task.status]} · {formatDate(task.created_at)} 创建
          </span>
          <div className="projectTimelineRow__members">
            {members.map((memberId) => {
              const name = data.getMemberName(memberId);
              return (
                <Tag key={memberId} title={name}>
                  {name}
                </Tag>
              );
            })}
          </div>
        </div>
      </div>
      <div className="projectTimelineRow__timeline" style={{ width: data.chartWidth }}>
        <div className="projectTimelineRow__guide" />
        {data.todayLeft !== undefined && (
          <div
            className="projectTimelineRow__today"
            style={{ left: data.todayLeft }}
          />
        )}
        <div
          className={`projectTimelineRow__bar projectTimelineRow__bar--${task.status} ${
            range.estimated
              ? "projectTimelineRow__bar--estimated"
              : "projectTimelineRow__bar--actual"
          }`}
          style={{ left, width }}
          title={`${task.title}：${formatDate(range.start)} 至 ${formatDate(range.end)}`}
        >
          <span>{rowNumber}</span>
          {task.title}
        </div>
      </div>
    </div>
  );
}

const ProjectTimelineView: FC<ProjectTimelineViewProps> = ({
  tasks,
  getMemberName,
  onTaskClick,
}) => {
  const [scale, setScale] = useState<TimelineScale>("week");
  const [keyword, setKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState<TimelineStatus | "all">("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [scrollLeft, setScrollLeft] = useState(0);
  const listOuterRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<VariableSizeList | null>(null);

  const assigneeOptions = useMemo(
    () =>
      uniqueValues(tasks.flatMap((task) => task.assignees)).map((userId) => ({
        label: getMemberName(userId),
        value: userId,
      })),
    [getMemberName, tasks]
  );

  const filteredTasks = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();
    return tasks.filter((task) => {
      const memberNames = task.assignees.map(getMemberName);
      const matchesKeyword =
        !normalizedKeyword ||
        task.title.toLowerCase().includes(normalizedKeyword) ||
        memberNames.some((name) => name.toLowerCase().includes(normalizedKeyword));
      const matchesStatus = statusFilter === "all" || task.status === statusFilter;
      const matchesAssignee =
        assigneeFilter === "all" || task.assignees.includes(assigneeFilter);
      return matchesKeyword && matchesStatus && matchesAssignee;
    });
  }, [assigneeFilter, getMemberName, keyword, statusFilter, tasks]);

  const ranges = useMemo(
    () => filteredTasks.map(getTaskTimelineRange),
    [filteredTasks]
  );
  const bounds = useMemo(() => getTimelineBounds(ranges), [ranges]);
  const spanDays = Math.max(1, (bounds.end - bounds.start) / TIMELINE_DAY_MS);
  const pixelsPerDay = scale === "day" ? 62 : scale === "week" ? 22 : 8;
  const chartWidth = Math.max(MIN_CHART_WIDTH, Math.ceil(spanDays * pixelsPerDay));
  const totalWidth = INFO_WIDTH + chartWidth;
  const todayLeft =
    Date.now() >= bounds.start && Date.now() <= bounds.end
      ? ((Date.now() - bounds.start) / (bounds.end - bounds.start)) * chartWidth
      : undefined;
  const tickDays = scale === "day" ? 1 : scale === "week" ? 7 : 30;
  const ticks = Array.from(
    { length: Math.min(160, Math.ceil(spanDays / tickDays) + 1) },
    (_, index) => {
      const timestamp = bounds.start + index * tickDays * TIMELINE_DAY_MS;
      return {
        timestamp,
        left: ((timestamp - bounds.start) / (bounds.end - bounds.start)) * chartWidth,
      };
    }
  );

  useEffect(() => {
    const outer = listOuterRef.current;
    if (!outer) return;
    const handleScroll = () => setScrollLeft(outer.scrollLeft);
    outer.addEventListener("scroll", handleScroll, { passive: true });
    return () => outer.removeEventListener("scroll", handleScroll);
  }, [filteredTasks.length, totalWidth]);

  useEffect(() => {
    listRef.current?.resetAfterIndex(0, true);
  }, [assigneeFilter, keyword, statusFilter, tasks]);

  const statusCounts = Object.keys(statusText).reduce(
    (result, status) => ({
      ...result,
      [status]: tasks.filter((task) => task.status === status).length,
    }),
    {} as Record<TimelineStatus, number>
  );

  const rowData: TimelineRowData = {
    bounds,
    chartWidth,
    getMemberName,
    onTaskClick,
    ranges,
    tasks: filteredTasks,
    todayLeft,
    totalWidth,
  };
  const listHeight = Math.min(560, Math.max(260, window.innerHeight - 430));

  return (
    <div className="projectTimelineView">
      <div className="projectTimelineView__toolbar">
        <div>
          <strong>当前阶段时间线</strong>
          <span>{filteredTasks.length} / {tasks.length} 个任务</span>
        </div>
        <div className="projectTimelineView__filters">
          <Input
            allowClear
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索任务或成员"
            prefix={<SearchOutlined />}
            value={keyword}
          />
          <Select
            onChange={setStatusFilter}
            options={[
              { label: "全部状态", value: "all" },
              ...Object.entries(statusText).map(([value, label]) => ({ label, value })),
            ]}
            value={statusFilter}
          />
          <Select
            onChange={setAssigneeFilter}
            options={[{ label: "全部负责人", value: "all" }, ...assigneeOptions]}
            showSearch
            value={assigneeFilter}
          />
          <div className="projectTimelineView__scale">
            {(["day", "week", "month"] as TimelineScale[]).map((item) => (
              <button
                className={scale === item ? "is-active" : ""}
                key={item}
                onClick={() => setScale(item)}
                type="button"
              >
                {{ day: "日", week: "周", month: "月" }[item]}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="projectTimelineView__summary">
        {(Object.keys(statusText) as TimelineStatus[]).map((status) => (
          <span
            className={`projectTimelineView__summaryItem projectTimelineView__summaryItem--${status}`}
            data-testid={`timeline-status-${status}`}
            key={status}
          >
            <i className={`is-${status}`} />
            <span className="projectTimelineView__summaryLabel">
              {statusText[status]}
            </span>
            <strong className="projectTimelineView__summaryCount">
              {statusCounts[status]}
            </strong>
          </span>
        ))}
      </div>

      {filteredTasks.length ? (
        <div className="projectTimelineView__table">
          <div className="projectTimelineView__header">
            <div className="projectTimelineView__headerInfo">
              <span>任务信息与全部参与成员</span>
              <small>时间区间</small>
            </div>
            <div className="projectTimelineView__headerDates">
              <div
                style={{
                  width: chartWidth,
                  transform: `translateX(${-scrollLeft}px)`,
                }}
              >
                {ticks.map((tick) => (
                  <span key={tick.timestamp} style={{ left: tick.left }}>
                    {formatDate(tick.timestamp)}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <VariableSizeList
            className="projectTimelineView__virtualList"
            height={listHeight}
            itemCount={filteredTasks.length}
            itemData={rowData}
            itemSize={(index) => getRowHeight(filteredTasks[index])}
            outerRef={listOuterRef}
            overscanCount={5}
            ref={listRef}
            width="100%"
          >
            {TimelineRow}
          </VariableSizeList>
          <div className="projectTimelineView__legend">
            <span><i className="is-actual" />实际完成区间</span>
            <span><i className="is-estimated" />预计完成区间</span>
            <small>点击任务信息或时间条查看节点详情</small>
          </div>
        </div>
      ) : (
        <div className="projectTimelineView__empty">当前筛选条件下没有任务</div>
      )}
    </div>
  );
};

export default ProjectTimelineView;
